import {
  collectionGroup,
  DocumentData,
  Firestore,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  where,
} from 'firebase/firestore';
import _ from 'lodash';
import moment from 'moment';
import { DriverInfo, FirestoreDriver, FireStoreRuleSet } from 'src/features/profile/types';
import { wasmCalculateEldState } from 'src/rulesetSystemWasmWrapper';
import { getTimerPercentage } from './timeHelper';

export function mapDataFromFirestoreDocs(data: FirestoreDriver): Map<string, FirestoreDriver> {
  const mergedData = new Map();

  const existingItem = mergedData.get(data.employeeId);
  if (existingItem) {
    // Merge existing and new items
    mergedData.set(data.employeeId, { ...existingItem, ..._.omit(data, 'type') });
  } else {
    // Add new item
    mergedData.set(data.employeeId, _.omit(data, 'type'));
  }

  return mergedData;
}

export async function queryDriverDocs(
  db: Firestore,
  driverInfo: DriverInfo,
  companyId: number,
): Promise<QueryDocumentSnapshot<DocumentData, DocumentData>[]> {
  const q = query(
    collectionGroup(db, 'driverStatusDomains'),
    where('type', '==', 'ELD-STATE'),
    where('status', '==', 'active'),
    where('companyId', '==', companyId),
    where('employeeId', '==', driverInfo.employeeId),
    orderBy('employeeId'),
    limit(1),
  );

  const result = await getDocs(q);
  return result.docs;
}

export async function queryDirectoryDataDocs(
  db: Firestore,
  driverInfo: DriverInfo,
): Promise<QueryDocumentSnapshot<DocumentData, DocumentData>[]> {
  let q = null;
  if (driverInfo.activeGroup) {
    q = query(
      collectionGroup(db, 'driverStatusDomains'),
      where('type', '==', 'DIRECTORY-DATA'),
      where('status', '==', 'active'),
      where('companyId', '==', driverInfo.companyId),
      where('groupIds', 'array-contains', driverInfo.activeGroup),
      orderBy('employeeId'),
      limit(1),
    );
  } else {
    q = query(
      collectionGroup(db, 'driverStatusDomains'),
      where('type', '==', 'DIRECTORY-DATA'),
      where('status', '==', 'active'),
      where('companyId', '==', driverInfo.companyId),
      orderBy('employeeId'),
      limit(1),
    );
  }

  const results = await getDocs(q);
  return results.docs;
}

export interface SnapshotState {
  latestRecord: { duration: number };
}

export async function calculateELDStateForData(data: Map<string, FirestoreDriver>) {
  for (const [, driver] of data.entries()) {
    if (driver?.['LATEST_STATE_SNAPSHOT'] || driver?.['CURRENT_LOG_STATE']) {
      if (driver?.['LATEST_STATE_SNAPSHOT']) {
        const inputELDRObj = _.clone(driver?.['LATEST_STATE_SNAPSHOT'].latestRecord);
        inputELDRObj.timestamp = moment.utc().toISOString(); // Update the eldr's timestamp to now so that regulations' current time values are calculated as: input eldr's time(present) - statesnapshot's eldr time(past)
        const inputELDR = JSON.stringify([inputELDRObj]);

        const driverConfig = JSON.stringify(driver?.['LATEST_STATE_SNAPSHOT'].configuration);
        const stateSnapshot = JSON.stringify(driver?.['LATEST_STATE_SNAPSHOT']);

        try {
          const response = await wasmCalculateEldState(
            inputELDR,
            driverConfig,
            stateSnapshot,
            JSON.stringify(''), // set projection time to ""
          );

          let updatedStateSnapshot;
          if (response) {
            // Timer calculations
            updatedStateSnapshot = JSON.parse(response).stateSnapshots?.find(
              (ss: SnapshotState) => ss.latestRecord.duration != 0,
            ); // use last snapshot with latestRecordDuration of 0 (meaning that there's no other record provided after it)
          }

          return updatedStateSnapshot;
        } catch (error) {
          console.log(error);
        }
      }
    }
  }
}

export interface RegulationData {
  text: string;
  time: number;
  percentage: number;
}

export const getRegulationsFromRuleset = (ruleset: FireStoreRuleSet): RegulationData[] => {
  const regulations = [];
  for (const regulation of ruleset.regulations) {
    const text = regulation.id.replace('_', ' ');
    const time = regulation.ceiling - regulation.current;
    const percentage = getTimerPercentage(regulation.current, regulation.ceiling);
    regulations.push({ text, time, percentage });
  }

  return regulations;
};
